import {
  action,
  computed,
  makeObservable,
  observable,
  ObservableMap,
  observe,
  runInAction,
} from 'mobx';
import RootStore from './RootStore';
import { IInquiry, IInquiryTarget } from '../interfaces/inquiry.interface';
import { CompleteStatusEnum } from '../interfaces/filters.interface';
import { jstMoment } from '../utils/date';
import { InquiryApi } from '../api/inquiry.api';
import { IRecipient, ISimpleRecipient } from '../interfaces/user.interface';
import { IOnUpdateTypingState } from '../interfaces/socket-event.interface';
import { handleRatPvEvent } from '../utils/rat';

export default class InquiryStore {
  rootStore: RootStore;

  // following variables must be given on each page.
  // because update on inquiryId is trigger for page transition.
  // if the id is same, Mobx does not fire the observer
  DUMMY_ID_FOR_NEW_INQUIRY_PAGE: string = 'NEW_INQUIRY';
  DUMMY_ID_FOR_INQUIRY_LIST_PAGE: string = 'INQUIRY_LIST_PAGE';

  @observable inquiries: ObservableMap<string, IInquiry> = observable.map();
  @observable recipients: ObservableMap<string, IRecipient> = observable.map();
  @observable nonRepliedCount: number = 0;
  @observable repliedCount: number = 0;
  @observable totalCount: number = -1;
  @observable activeInquiryId: string = '';
  @observable activeInquiry: IInquiry | null = null;
  @observable inquiryTarget: IInquiryTarget = {
    categoryId: -1,
    targetName: '',
  };

  @observable loadedInquiryNumber = 0;
  @observable hasMoreInquiry = true;

  @observable isRecipientTypingMessage: boolean = false;

  @action
  updateRecipientSideState = (stateBeacon: IOnUpdateTypingState) => {
    switch (stateBeacon.listeningEventType) {
      case 'blur':
        this.isRecipientTypingMessage = false;
        break;
      case 'focus':
        this.isRecipientTypingMessage =
          this.activeInquiryId === stateBeacon.inquiryId;
        break;
    }
  };

  @action
  setActiveInquiryFromInquiries = () => {
    const activeInquiry = this.inquiries.get(this.activeInquiryId);
    if (activeInquiry) {
      runInAction(() => {
        this.activeInquiry = activeInquiry;
      });
    }
  };

  @computed
  get inquiryRecipients(): Array<ISimpleRecipient> {
    // inquiry must exist when recipients are referred
    return this.activeInquiry!.recipients;
  }

  // @computed
  // get validateCategories(): boolean {
  //   return !this.activeCategoryId && !this.activeInquiryId;
  // }

  @computed
  get isInquiryReady(): boolean {
    return (
      this.activeInquiryId === this.DUMMY_ID_FOR_NEW_INQUIRY_PAGE ||
      this.activeInquiryId === this.activeInquiry?.inquiryId
    );
  }

  get isNewInquiryPage(): boolean {
    return (
      // FIXME: status definition should be based on values in Store...
      this.rootStore.routerStore.history.location.pathname === '/inquiry/new'
    );
  }

  @computed
  get filteredInquiries(): IInquiry[] {
    const { appliedFilters, isSortedByLastMessage } =
      this.rootStore.searchStore;
    const { showCompleteButton } = this.rootStore.serviceStore;

    if (!this.inquiries) {
      return [];
    }
    return Array.from(this.inquiries)
      .filter((inquiryMapArray: [string, IInquiry]) => {
        const inquiry = inquiryMapArray[1];

        // Date Range
        const key = isSortedByLastMessage
          ? 'lastMessageTimestamp'
          : 'initialMessageTimestamp';
        const timestamp = jstMoment(inquiry[key]).format('YYYY-MM-DDTHH:mm:ss');
        let from: string;
        let to: string;
        if (appliedFilters.date.isCleared) {
          const toDate = jstMoment().endOf('day');
          const range = Number(appliedFilters.date.clearedType.split('_')[0]);
          let span = 'year';
          switch (appliedFilters.date.clearedType.split('_')[1]) {
            case 'MONTH':
              span = 'month';
              break;
            case 'WEEK':
              span = 'week';
              break;
            default:
              span = 'year';
          }
          const fromDate = toDate
            .clone()
            .subtract(range, span as 'year' | 'month' | 'week')
            .startOf('day');
          from = fromDate.format('YYYY-MM-DDTHH:mm:ss');
          to = toDate.format('YYYY-MM-DDTHH:mm:ss');
        } else {
          from = appliedFilters.date.from;
          to = appliedFilters.date.to;
        }
        const isInDateRange = timestamp >= from && timestamp <= to;
        if (!isInDateRange) {
          return false;
        }

        if (appliedFilters.completeStatus === CompleteStatusEnum.All) {
          return true;
        }

        // Complete & Reply Status
        if (
          showCompleteButton &&
          appliedFilters.completeStatus === CompleteStatusEnum.Complete
        ) {
          return inquiry.completeStatus === true;
        }

        if (
          showCompleteButton &&
          appliedFilters.completeStatus === CompleteStatusEnum.NotComplete
        ) {
          if (
            appliedFilters.replyStatus.nonReplied &&
            !appliedFilters.replyStatus.replied
          ) {
            return (
              inquiry.completeStatus === false &&
              inquiry.lastReplyStatus === false
            );
          }
          if (
            !appliedFilters.replyStatus.nonReplied &&
            appliedFilters.replyStatus.replied
          ) {
            return (
              inquiry.completeStatus === false &&
              inquiry.lastReplyStatus === true
            );
          }
          return inquiry.completeStatus === false;
        }

        if (!showCompleteButton) {
          if (
            appliedFilters.replyStatus.nonReplied &&
            !appliedFilters.replyStatus.replied
          ) {
            return inquiry.lastReplyStatus === false;
          }
          if (
            !appliedFilters.replyStatus.nonReplied &&
            appliedFilters.replyStatus.replied
          ) {
            return inquiry.lastReplyStatus === true;
          }
        }

        return true;
      })
      .map((inquiryMapArray: [string, IInquiry]) => {
        return inquiryMapArray[1];
      });
  }

  @action
  // count: called count InfiniteScroll has called (good for pagination or else)
  loadMore = async (count: number) => {
    const offset = 10; //FIXME: change it to 100 (or more?) when Demo is done
    runInAction(() => {
      console.log(
        `loading: ${this.loadedInquiryNumber} + ${offset} of ${this.filteredInquiries.length}`,
      );
      this.loadedInquiryNumber = this.loadedInquiryNumber + offset;
      this.hasMoreInquiry =
        this.loadedInquiryNumber < this.filteredInquiries.length;
      console.log(
        `loaded: ${this.loadedInquiryNumber}, hasMoreRow: ${this.hasMoreInquiry}`,
      );
    });
  };

  @action
  initializeStore = () => {
    this.inquiries = observable.map();
    this.totalCount = -1;
    this.activeInquiry = null;
    this.inquiryTarget = {
      categoryId: -1,
      targetName: '',
    };
  };

  @action
  validateActiveInquiryId = () => {
    return this.activeInquiryId !== this.DUMMY_ID_FOR_NEW_INQUIRY_PAGE; // same as return this.activeInquiryId ? true : false;
  };

  @action
  onMessageSent = async (
    inquiryId: string,
    messageBody: string,
    messageSentTimestamp: string,
  ) => {
    if (this.activeInquiry && this.activeInquiry.inquiryId === inquiryId) {
      runInAction(() => {
        this.activeInquiry = {
          ...this.activeInquiry!,
          lastReplyStatus: true,
          lastReadStatus: true,
          lastMessage: messageBody,
          lastMessageTimestamp: messageSentTimestamp,
        };
      });
    }
    // if (this.rootStore.searchStore.hasKeyword) {
    //   await this.fetchInquiryData(false);
    //   return;
    // }
    const before = this.filteredInquiries.length;
    // future expansion when rmp-front creates message
    // if (this.inquiries.get(inquiryId)) {
    //   runInAction(() => {
    //     this.inquiries.set(inquiryId, {
    //       ...this.inquiries.get(inquiryId)!,
    //       lastReplyStatus: true,
    //       lastReadStatus: true,
    //       lastMessage: messageBody,
    //       lastMessageTimestamp: messageSentTimestamp,
    //     });
    //   });
    // } else {
    //   await this.fetchOneInquiryData(inquiryId);
    // }
    const after = this.filteredInquiries.length;
    runInAction(() => {
      this.totalCount += after - before;
    });
  };

  @action
  onMessageReceived = async (
    inquiryId: string,
    messageBody: string,
    messageSentTimestamp: string,
  ) => {
    if (this.activeInquiry && this.activeInquiry.inquiryId === inquiryId) {
      runInAction(() => {
        this.activeInquiry = {
          ...this.activeInquiry!,
          lastReplyStatus: false,
          lastReadStatus: false,
          completeStatus: false,
          lastMessage: messageBody,
          lastMessageTimestamp: messageSentTimestamp,
        };
      });
    }
    const before = this.filteredInquiries.length;
    if (this.inquiries.get(inquiryId)) {
      runInAction(() => {
        this.inquiries.set(inquiryId, {
          ...this.inquiries.get(inquiryId)!,
          lastReplyStatus: false,
          lastReadStatus: false,
          completeStatus: false,
          lastMessage: messageBody,
          lastMessageTimestamp: messageSentTimestamp,
        });
      });
    } else {
      // future expansion when rmp-front creates message
      // await this.fetchOneInquiryData(inquiryId);
    }
    const after = this.filteredInquiries.length;
    runInAction(() => {
      this.totalCount += after - before;
    });
  };

  @action
  fetchInquiryList = async (setInquiryLoading: boolean = true) => {
    // IMPORTANT! activeInquiryId is initialized when fetching inquiry list
    this.activeInquiryId = this.DUMMY_ID_FOR_INQUIRY_LIST_PAGE;
    this.initializeStore();

    this.rootStore.inquiryPageStore.setIsLoadingInquiryData(true);

    try {
      const { appliedFilters } = this.rootStore.searchStore;
      const { inquiries, recipients } = await InquiryApi.searchInquiries(
        appliedFilters,
      );
      runInAction(() => {
        inquiries.records.forEach((inquiry) => {
          this.inquiries.set(inquiry.inquiryId, inquiry);
        });
        recipients.forEach((recipient) => {
          this.recipients.set(recipient.userId, recipient);
        });
        this.totalCount = inquiries.metadata.total;
      });
    } catch (e: any) {
      console.error(e);
      if (e.status !== 404 /* "NOT" NOT FOUND */) {
        window.location.href = '/50x';
        return;
      }
    }
    this.rootStore.inquiryPageStore.setIsLoadingInquiryData(false);
  };

  // for future expansion
  // @action
  // fetchInquiryCountData = async () => {
  //   const { user } = this.rootStore.userStore;
  //   if (!user.userId || this.rootStore.inquiryPageStore.isLoadingInquiryData) {
  //     return;
  //   }
  //   this.rootStore.inquiryPageStore.setIsLoadingInquiryCountData(true);
  //   try {
  //     const { appliedFilters } = this.rootStore.searchStore;
  //     const res = await InquiryApi.searchInquiriesCount(appliedFilters);
  //     runInAction(() => {
  //       this.totalCount = res.count;
  //     });
  //   } catch (e) {
  //     console.error(e);
  //     this.rootStore.inquiryPageStore.setFailedLoadingInquiryData(true);
  //   }
  //   this.rootStore.inquiryPageStore.setIsLoadingInquiryCountData(false);
  // };

  // for future expansion
  // @action
  // fetchOneInquiryData = async (inquiryId: string) => {
  //   const { user } = this.rootStore.userStore;
  //   if (!user.userId) {
  //     return;
  //   }
  //   const res = await InquiryApi.getOneInquiry(inquiryId);
  //   runInAction(() => {
  //     this.setOneInquiry(res);
  //   });
  // };

  @action
  updateInquiryLastReadStatus = (inquiryId: string) => {
    const inquiry = this.inquiries.get(inquiryId);
    if (inquiry) {
      runInAction(() => {
        this.inquiries.set(inquiryId, {
          ...inquiry!,
          lastReadStatus: true,
        });
      });
    }
  };

  @computed
  get nonRepliedExists(): boolean {
    return this.nonRepliedCount > 0;
  }

  @action
  setActiveInquiryId = (activeInquiryId: string) => {
    this.activeInquiryId = activeInquiryId;
  };

  @action
  observeActiveInquiryId = async (change: any) => {
    const newActiveInquiryId = change.newValue;

    if (
      [
        this.DUMMY_ID_FOR_NEW_INQUIRY_PAGE,
        this.DUMMY_ID_FOR_INQUIRY_LIST_PAGE,
      ].includes(newActiveInquiryId)
    ) {
      handleRatPvEvent(
        this.rootStore.userStore,
        this.rootStore.inquiryStore,
        this.rootStore.partnerStore!,
      );
      return;
    }

    console.log('InquiryStore:observeActiveInquiryId');
    console.log('observeActiveInquiryId', { to: this.activeInquiryId });

    this.rootStore.chatInputStore.resetInput(); // remove validation message before transition
    this.rootStore.routerStore.push(
      `/inquiry/${newActiveInquiryId}${window.location.search}`,
    );

    // inquiry must be here as result of fetch
    const { inquiry, recipients } =
      (await this.rootStore.messageStore.fetchMessageData(newActiveInquiryId))!;

    if (!(inquiry && recipients)) {
      console.error('failed fetch message data.');
      return;
    }

    this.activeInquiry = { ...inquiry, recipients: recipients };

    await this.rootStore.faqStore.createFaqSummary(
      recipients[0], //multiple recipients with one inquiry is not supported
      this.activeInquiry.inquiryCategory,
    );

    this.activeInquiry.targetId &&
      (await this.rootStore.partnerStore?.setTargetDetail(
        recipients[0], //multiple recipients with one targetId is not supported
        this.activeInquiry.targetId,
      ));

    // this.rootStore.memoStore.initMemo();
    // this.rootStore.chatInputStore.adjustInputTextareaHeight();

    handleRatPvEvent(
      this.rootStore.userStore,
      this.rootStore.inquiryStore,
      this.rootStore.partnerStore!,
    );
  };

  constructor(rootStore: RootStore) {
    console.log('InquiryStore:constructor');
    this.rootStore = rootStore;
    makeObservable(this);
    observe(this, 'activeInquiryId', this.observeActiveInquiryId);
  }
}
